/**
 * Header / Navigation Component
 *
 * Renders the top navigation bar with:
 * - Kidsland logo (from Sanity)
 * - Main navigation menu with dropdowns
 * - Language switcher (English / 中文)
 *
 * Navigation structure:
 * - Framed pages (iframe → kidsland.com.cn): 關於我們, 新聞資訊, 合作品牌, 零售體系, 分銷體系
 * - Internal Gatsby pages: 投資者關係
 */
import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { css, Global } from '@emotion/react'
import styled from '@emotion/styled'
import { LangKey } from '../types'
import { getAlternateLangPath } from '../utils/lang'

interface HeaderProps {
  langKey: LangKey
  isFixed?: boolean
  currentPath?: string
}

// ─── Styled Components ────────────────────────────────────────────────────────

const LangSwitcher = styled.div`
  width: 66.67px;
  background-color: #f4f4f4;
  margin-right: 2.35rem;
  height: 26px;
  border: 1px solid #b2b2b2;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  box-shadow: -5px 5px 10px -4px #000;
  -webkit-box-shadow: 0 5px 20px -9px #000;
  -moz-box-shadow: -5px 5px 10px -4px #000;

  a {
    color: #979797;
    text-decoration: none;
    cursor: pointer !important;
  }
`

const LangBar = styled.div`
  width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 767px) {
    display: none;
  }
`

const LangBarWrapper = styled.div<{ isFixed: boolean }>`
  position: ${({ isFixed }) => (isFixed ? 'fixed' : 'absolute')};
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  z-index: 31;
`

const StyledHeader = styled.header<{ isFixed: boolean }>`
  position: ${({ isFixed }) => (isFixed ? 'fixed' : 'absolute')};
  top: 0;
  right: 0;
  left: 0;
  width: ${({ isFixed }) => (isFixed ? '100%' : 'calc(100% - 0.7rem)')};
  z-index: 10;
  background-color: #fff;
  color: #161616;
  font-size: 1rem;
  height: 120px;
  padding: 2rem 0.5rem;
  margin-left: 0;

  nav {
    height: 100%;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  nav img {
    margin-left: 46px;
    width: 149px;
    height: 59px;
  }

  nav ul {
    list-style: none;
    margin: 0.5rem 0.5rem 0 0;
    padding-left: 0;
    text-transform: capitalize;
  }

  nav ul li {
    margin-top: -0.5rem;
    display: block;
    float: left;
    padding: 1rem 1.55rem;
    position: relative;
    text-decoration: none;
    text-transform: capitalize;
    transition-duration: 0.5s;
  }

  nav ul li:hover {
    cursor: pointer;
  }

  nav ul li:hover ul {
    visibility: visible;
    opacity: 1;
    display: block;
  }

  nav ul li:hover ul li:hover {
    color: red;
  }

  nav ul li ul {
    visibility: hidden;
    opacity: 0;
    border-radius: 0.5rem;
    width: 100%;
    min-width: 5rem;
    position: absolute;
    transition: all 0.5s ease;
    margin-top: 1.5rem;
    left: 0;
    display: none;
    text-transform: capitalize;
  }

  nav ul li ul li {
    background: rgba(255, 255, 255, 1);
    padding: 1rem 0.5rem;
    clear: both;
    width: 100%;
    text-align: center;
    font-size: 0.9rem;
    text-transform: capitalize;
  }

  nav ul li ul li:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  nav a {
    text-decoration: none;
    color: inherit;
  }

  @media (max-width: 767px) {
    width: 100%;
    height: 76px;
    padding: 0.75rem 0.25rem;
    font-size: 8px;

    nav {
      max-width: 100%;
      height: 100%;
      gap: 0.4rem;
      align-items: center;
      justify-content: flex-start;
      overflow: visible;
    }

    nav > a {
      flex: 0 0 86px;
      display: flex;
      align-items: center;
      padding-left: 0.75rem;
    }

    nav img {
      width: 68px;
      height: auto;
      margin-left: 0;
      display: block;
    }

    nav ul {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex: 1 1 auto;
      margin: 0;
      padding: 0;
      min-width: 0;
      overflow-x: visible;
      overflow-y: visible;
      white-space: nowrap;
      gap: 0;
    }

    nav ul li {
      float: none;
      flex: 0 0 auto;
      margin-top: 0;
      padding: 0.5rem 0.25rem;
      white-space: nowrap;
    }

    nav ul li ul {
      margin-top: 0.5rem;
      min-width: 4.5rem;
      z-index: 20;
    }

    nav ul li ul li {
      padding: 0.6rem 0.35rem;
      font-size: 8px;
      white-space: normal;
    }
  }
`

// ─── Component ────────────────────────────────────────────────────────────────

const Header: React.FC<HeaderProps> = ({ langKey, isFixed = true, currentPath = '/' }) => {
  const data = useStaticQuery(graphql`
    query HeaderQuery {
      sanitySiteSettings {
        logo {
          asset {
            url
          }
        }
      }
    }
  `)

  const logoUrl =
    data?.sanitySiteSettings?.logo?.asset?.url ||
    'https://cdn.sanity.io/images/s1gua7do/production/2e0db828992d8f53c0dd0584f4886d96197c5c0f-634x251.png'

  const alternatePath = getAlternateLangPath(currentPath, langKey)
  const altLangLabel = langKey === 'en' ? '中文' : 'English'

  // Navigation structure
  const externalNav = langKey === 'zh-hant'
    ? [
        {
          label: '關於我們',
          items: [
            { label: '企業介紹', href: '/overview' },
            { label: '發展歷程', href: '/development-history' },
            { label: '董事會成員', href: '/board-members' },
            { label: '管理團隊', href: '/management-team' },
            { label: '聯繫我們', href: '/contact-us' },
          ],
        },
        {
          label: '新聞資訊',
          items: [{ label: '新聞動態', href: '/recent-news' }],
        },
        {
          label: '合作品牌',
          items: [
            // { label: '潮流玩具', href: '/trendy-toys' },
            { label: '玩具', href: '/classic-toys' },
            // { label: '嬰童用品', href: '/babycare' },
          ],
        },
        {
          label: '零售體系',
          items: [
            { label: '零售品牌', href: '/brand-stores' },
            { label: '百貨專櫃', href: '/find-a-store' },
          ],
        },
        {
          label: '分銷體系',
          items: [
            { label: '分銷網絡', href: '/distribution-network' },
            { label: '電商平台', href: '/ecommerce' },
          ],
        },
        {
          label: '投資者關係',
          items: [
            { label: '公告與通函', href: '/announcements-and-circulars' },
            { label: '財務報告', href: '/financial-reports' },
            { label: '企業管治', href: '/corporate-governance' },
            { label: '新聞發布', href: '/press-releases' },
            { label: '投資者關係聯絡', href: '/investors-relations-contact' },
          ],
        },
      ]
    : [
        {
          label: 'About us',
          items: [
            { label: 'Overview', href: '/en/overview' },
            { label: 'Development History', href: '/en/development-history' },
            { label: 'Board Members', href: '/en/board-members' },
            { label: 'Management Team', href: '/en/management-team' },
            { label: 'Contact Us', href: '/en/contact-us' },
          ],
        },
        {
          label: 'News',
          items: [{ label: 'Recent News', href: '/en/recent-news' }],
        },
        {
          label: 'Brand Category',
          items: [
            // { label: 'Trendy Toys', href: '/en/trendy-toys' },
            { label: 'Toys', href: '/en/classic-toys' },
            // { label: 'Babycare', href: '/en/babycare' },
          ],
        },
        {
          label: 'Retail',
          items: [
            { label: 'Brand Stores', href: '/en/brand-stores' },
            { label: 'Find a store', href: '/en/find-a-store' },
          ],
        },
        {
          label: 'Distribution',
          items: [
            { label: 'Distribution Network', href: '/en/distribution-network' },
            { label: 'Ecommerce', href: '/en/ecommerce' },
          ],
        },
        {
          label: 'Investors Relations',
          items: [
            { label: 'Announcements and Circulars', href: '/en/announcements-and-circulars' },
            { label: 'Financial Reports', href: '/en/financial-reports' },
            { label: 'Corporate Governance', href: '/en/corporate-governance' },
            { label: 'Press Releases', href: '/en/press-releases' },
            { label: 'Investors Relations Contact', href: '/en/investors-relations-contact' },
          ],
        },
      ]

  const homeHref = langKey === 'en' ? '/en/' : '/'

  return (
    <>
      <LangBarWrapper isFixed={isFixed}>
        <LangBar>
          <LangSwitcher>
            <a href={alternatePath}>{altLangLabel}</a>
          </LangSwitcher>
        </LangBar>
      </LangBarWrapper>

      <StyledHeader isFixed={isFixed}>
        <nav role="navigation">
          <a href={homeHref}>
            <img src={logoUrl} alt="Kidsland" />
           </a>
          <ul>
            {externalNav.map((section) => (
              <li key={section.label}>
                {section.label}
                <ul>
                  {section.items.map((item) => (
                    <li key={item.href}>
                      <a href={item.href}>{item.label}</a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
      </StyledHeader>
    </>
  )
}

export default Header
